import { useEffect, useMemo } from "react";
import { useParams } from "react-router";

import { FilterButton, MasterDetailLayout } from "common/components";
import { getPageId } from "common/utilities";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { applyKnowledgeDataSearchCriteria } from "domains/knowledgeData/actions";
import {
  createInteractiveDataForTopicSelector,
  getInteractiveDataFilterCount,
  getIsKnowledgeDataFiltered,
  getKnowledgeDataSearchTerm,
} from "domains/knowledgeData/selectors";
import { activateModal } from "domains/modal/actions";
import { KnowledgeDetail } from "domains/publication/components/KnowledgeDetail";
import { KnowledgeList } from "domains/publication/components/KnowledgeList";
import { getTopicPageEvent, getTopicRelatedItemDetailPageEvent } from "domains/topic/analytics";
import { getTopicTitle, useTopicNavigation } from "domains/topicDetail/utils";
import { TopicSearchBar } from "domains/topicSearchFilter/components/TopicSearchBar";
import { useTopicContentMeta } from "domains/topicSearchFilter/useTopicContentMeta";
import { createKnowledgesSelector, getKnowledgeById } from "entities/knowledge/selectors";
import { getCanSeeSectionContent } from "entities/permissions/selectors";
import { fetchTopic } from "entities/topics/actions";
import {
  createHasUnauthenticatedContentAccessSelector,
  createTopicSelector,
  getTopic,
} from "entities/topics/selectors";

export const TopicKnowledgeDataPage = () => {
  const dispatch = useDispatch();
  const { keyIssueId, knowledgeId, topicId } = useParams();
  const hasAccessToContent = useSelector(getCanSeeSectionContent);
  const filterCount = useSelector(getInteractiveDataFilterCount);
  const isFiltered = useSelector(getIsKnowledgeDataFiltered);
  const searchTerm = useSelector(getKnowledgeDataSearchTerm);
  const { hasFilters } = useTopicContentMeta("data");
  const track = useTrack();
  const translate = useTranslate();

  if (topicId === undefined) throw new Error("topicId param undefined in TopicKnowledgeDataPage");
  const selectedTopicId = keyIssueId ?? topicId;

  const unauthenticatedSectionAccess = useSelector(createHasUnauthenticatedContentAccessSelector(topicId));
  const interactiveDataForTopic = useSelector(createInteractiveDataForTopicSelector(selectedTopicId));
  const topic = useSelector(createTopicSelector(topicId));
  const keyIssue = useSelector(getTopic(keyIssueId));
  const { path } = useTopicNavigation({ id: topicId, keyIssueId });

  const knowledgesSelector = useMemo(
    () => createKnowledgesSelector(interactiveDataForTopic?.data ?? []),
    [interactiveDataForTopic?.data],
  );

  const interactiveData = useSelector(knowledgesSelector);

  const id = getPageId(knowledgeId, interactiveData);

  const knowledge = useSelector(getKnowledgeById(id));

  const hasNextPage = interactiveDataForTopic?.hasNextPage ?? false;
  const isPending = interactiveDataForTopic?.isFetching ?? false;
  const isLocked = !hasAccessToContent && !unauthenticatedSectionAccess;
  const selectedTopic = keyIssueId ? keyIssue : topic;

  useEffect(() => {
    if (!topic || (keyIssueId && !keyIssue)) {
      return;
    }
    const event = getTopicPageEvent(topic, keyIssue, "Interactive Data List");
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyIssue?.id, topic?.id, track]);

  useEffect(() => {
    if (!selectedTopic || !knowledge) {
      return;
    }
    const event = getTopicRelatedItemDetailPageEvent(
      "Interactive Data Detail",
      { id: knowledge.id, name: knowledge.title },
      "Interactive data",
      selectedTopic,
    );
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledge?.id, selectedTopic?.id, track]);

  useEffect(() => {
    if (interactiveData.length === 0 || isPending) {
      dispatch(
        applyKnowledgeDataSearchCriteria({
          topicId: selectedTopicId,
          offset: 0,
          limit: 20,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId, hasAccessToContent]);

  useEffect(() => {
    if (!selectedTopic) {
      dispatch(fetchTopic({ id: topicId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  const getLink = (knowledgeId: string) => `${path}/interactive-data/${knowledgeId}`;

  const loadNextPage = () => {
    dispatch(
      applyKnowledgeDataSearchCriteria({
        topicId: selectedTopicId,
        offset: interactiveData.length,
        limit: 20,
      }),
    );
  };

  const onFilterClick = () => dispatch(activateModal({ id: "TopicDataFilter", params: { selectedTopicId, topicId } }));

  let emptyMessage = isFiltered
    ? translate("app.monitor.empty_message_filter")
    : `${translate("app.topic.no_content")} ${translate("app.discover.topic.tabs.interactive_data.title")}.`;

  if (!topic?.id) {
    emptyMessage = translate("app.no_access.topic_content");
  }

  const topicFilterButton =
    hasFilters && topic?.id && !isLocked ? <FilterButton count={filterCount} onClick={onFilterClick} /> : undefined;

  const knowledgeList = (
    <KnowledgeList
      items={interactiveData}
      selectedId={id}
      trackPage="Discover"
      getToLink={getLink}
      type="medium"
      loadNextPage={!isLocked ? loadNextPage : undefined}
      hasNextPage={!isLocked ? hasNextPage : false}
      isPending={isPending}
      lockTiles={isLocked}
      emptyMessage={emptyMessage}
    />
  );

  const shareLink = `${import.meta.env.VITE_WEBSITE_URL}/topics/${topicId}/interactive-data/${knowledge ? knowledge.id : ""}`;

  const title = translate("app.discover.topic.tabs.interactive_data.title");

  const onSearchHandle = (search: string) =>
    dispatch(
      applyKnowledgeDataSearchCriteria({
        topicId: selectedTopicId,
        search,
      }),
    );

  const searchElement =
    topic?.id && !isLocked ? (
      <TopicSearchBar onSearch={onSearchHandle} isPending={isPending} defaultValue={searchTerm} />
    ) : undefined;

  return (
    <MasterDetailLayout
      listTitle={title}
      listComponent={knowledgeList}
      listBackNavigation={{
        label: getTopicTitle(topic, keyIssue) ?? "",
        path,
      }}
      detailBackNavigation={{
        label: title,
        path: `${path}/interactive-data`,
      }}
      mainView={knowledgeId ? "right" : "left"}
      headerElement={searchElement}
      listHeaderRightElement={topicFilterButton}
    >
      <KnowledgeDetail
        id={id}
        knowledge={knowledge}
        shareLink={shareLink}
        topicBack={{ term: "app.discover.topic.tabs.interactive_data.title" }}
      />
    </MasterDetailLayout>
  );
};
